import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import type { User, AuthResponse, LoginCredentials, RefreshTokenRequest, RefreshTokenResponse } from '@/types/auth';
import type { AuthStorage } from './storage';
import { environment as envConfig } from '@/config/environment';
import localizationService from '../services/localizationService';

export interface AuthConfig {
  apiBaseUrl: string;
  storage: AuthStorage;
}

export interface AuthContextType {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  handleAuthResponse: (response: AuthResponse) => Promise<void>;
  isTokenValid: () => boolean;
  refreshTokens: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function createAuthProvider(config: AuthConfig) {
  function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [refreshToken, setRefreshToken] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const isRefreshing = useRef(false);

    // Function to validate JWT token
    const isTokenValid = useCallback((): boolean => {
      if (!token) return false;

      try {
        // Parse JWT token
        const parts = token.split('.');
        if (parts.length !== 3) {
          console.error('Invalid JWT format');
          return false;
        }

        const payload = JSON.parse(atob(parts[1]));
        const currentTime = Math.floor(Date.now() / 1000);

        // Check expiration
        if (payload.exp && payload.exp <= currentTime) {
          console.warn('JWT token has expired');
          return false;
        }

        // Check not before
        if (payload.nbf && payload.nbf > currentTime) {
          console.warn('JWT token not yet valid');
          return false;
        }

        // Note: Angular doesn't validate issuer/audience, so we'll skip those checks
        // to match Angular's behavior and allow OAuth2 tokens with any iss/aud values

        return true;
      } catch (error) {
        console.error('Error validating JWT token:', error);
        return false;
      }
    }, [token]);

    // Function to refresh tokens
    const refreshTokens = useCallback(async (): Promise<boolean> => {
      // Prevent multiple simultaneous refresh attempts
      if (isRefreshing.current) {
        console.log('Token refresh already in progress, skipping...');
        return false;
      }

      
      isRefreshing.current = true;
      let result = false;

      try {
        console.log('Refreshing tokens...');
        const response = await fetch(envConfig.apiPrefix.rootPath + `auth/refresh`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: refreshToken || '' } as RefreshTokenRequest),
        });

        let token = '';
        let refresh = '';
        if (response && response.ok) {
          const refreshResponse: RefreshTokenResponse = await response.json();
          token = refreshResponse.token;
          refresh = refreshResponse.refreshToken;
        }

        // Store new tokens
        await Promise.all([
          config.storage.setToken(token),
          config.storage.setRefreshToken(refresh),
        ]);

        setToken(token);
        setRefreshToken(refresh);

        // // Sync read-only from token after refresh
        // if (token && token.length > 0) {
        //   ReadOnlyService.setReadOnly(getIsReadOnlyByStatusNToken(token));
        // }

        console.log('Tokens refreshed successfully');

        result = true;
      } catch (error) {
        console.error('Token refresh failed:', error);
      } finally {
        isRefreshing.current = false;
      }

      return result;
    }, [refreshToken]);

    // Function to clear auth state
    const clearAuthState = useCallback(async () => {
      try {
        await Promise.all([
          config.storage.removeToken(),
          config.storage.removeRefreshToken(),
          config.storage.removeUser(),
          // by removing on logout, we ensure that server issues a new session id on next request
          // then redis fetching that uses keys like '{sessionid}{someString}' will not be getting data
          // from previous session, this is particularly problematic with the prospect profile
          config.storage.removeYsiSessionID(),
        ]);
        setToken(null);
        setRefreshToken(null);
        setUser(null);
      } catch (error) {
        console.error('Failed to clear auth state:', error);
      }
    }, []);

    // Initialize auth state from storage
    useEffect(() => {
      async function initAuth() {
        try {
          console.log('Initializing auth state from storage...');
          const [storedToken, storedRefreshToken, storedUser] = await Promise.all([
            config.storage.getToken(),
            config.storage.getRefreshToken(),
            config.storage.getUser(),
          ]);

          console.log('Retrieved from storage:', {
            storedToken: !!storedToken,
            storedRefreshToken: !!storedRefreshToken,
            storedUser: !!storedUser
          });

          if (storedToken && storedUser) {
            setToken(storedToken);
            setRefreshToken(storedRefreshToken);

            // Validate the stored token
            const tempToken = storedToken;
            const isValid = (() => {
              if (!tempToken) return false;
              try {
                const parts = tempToken.split('.');
                if (parts.length !== 3) return false;
                const payload = JSON.parse(atob(parts[1]));
                const currentTime = Math.floor(Date.now() / 1000);

                return payload.exp ? payload.exp > currentTime : true;
              } catch {
                return false;
              }
            })();

            if (isValid) {
              console.log('Setting valid auth state from storage');
              setUser(storedUser);
            } else if (storedRefreshToken) {
              console.log('Stored token is invalid, attempting refresh...');
              try {
                setRefreshToken(storedRefreshToken);
                await refreshTokens();
                setUser(storedUser);
              } catch (error) {
                console.log('Token refresh failed, clearing auth state');
                await clearAuthState();
              }
            } else {
              console.log('Stored token is invalid and no refresh token, clearing auth state');
              await clearAuthState();
            }
          } else {
            console.log('No stored auth state found');
          }
        } catch (error) {
          console.error('Failed to initialize auth:', error);
          await clearAuthState();
        } finally {
          setIsLoading(false);
        }
      }

      initAuth();
    }, [clearAuthState, refreshTokens]);

    // Set up automatic token validation and refresh
    useEffect(() => {
      if (!token || isLoading) return;

      // Check token validity immediately
      if (!isTokenValid()) {
        if (refreshToken) {
          console.log('Token is invalid, attempting refresh...');
          refreshTokens().catch(() => {
            console.log('Token refresh failed, logging out automatically');
            clearAuthState();
          });
        } else {
          console.log('Token is invalid and no refresh token, logging out automatically');
          clearAuthState();
        }
        return;
      }

      // Set up interval to check token expiry and refresh proactively
      const interval = setInterval(() => {
        if (!isTokenValid()) {
          if (refreshToken) {
            console.log('Token expired, attempting refresh...');
            refreshTokens().catch(() => {
              console.log('Token refresh failed, logging out automatically');
              clearAuthState();
            });
          } else {
            console.log('Token expired and no refresh token, logging out automatically');
            clearAuthState();
          }
        }
      }, 60000); // Check every minute

      return () => clearInterval(interval);
    }, [token, refreshToken, isLoading, isTokenValid, refreshTokens, clearAuthState]);

    const handleAuthResponse = async (response: AuthResponse) => {
      try {
        console.log('Handling auth response:', {
          hasToken: !!response.token,
          hasRefreshToken: !!response.refreshToken,
          hasUser: !!response.user,
          userEmail: response.user?.email,
          tokenPreview: response.token ? response.token.substring(0, 20) + '...' : 'none'
        });

        if (!response.token || !response.user) {
          console.error('Invalid auth response:', response);
          throw new Error('Invalid auth response: missing token or user');
        }

        // Validate the new token before storing
        const tempToken = response.token;
        console.log('Validating JWT token structure...');

        const isValid = (() => {
          try {
            const parts = tempToken.split('.');
            if (parts.length !== 3) {
              console.error('JWT validation failed: Invalid format, expected 3 parts, got', parts.length);
              return false;
            }

            const payload = JSON.parse(atob(parts[1]));
            console.log('JWT payload:', {
              iss: payload.iss,
              aud: payload.aud,
              exp: payload.exp,
              iat: payload.iat,
              hasExpClaim: !!payload.exp
            });

            const currentTime = Math.floor(Date.now() / 1000);

            // Check expiration
            if (payload.exp && payload.exp <= currentTime) {
              console.error('JWT validation failed: Token expired', {
                exp: payload.exp,
                currentTime,
                expiredAgo: currentTime - payload.exp
              });
              return false;
            }

            // Note: Angular doesn't validate issuer/audience, so we'll skip those checks
            // to match Angular's behavior and allow OAuth2 tokens with any iss/aud values

            console.log('JWT validation passed successfully');
            return true;
          } catch (parseError) {
            console.error('JWT validation failed: Parse error', parseError);
            return false;
          }
        })();

        if (!isValid) {
          console.error('JWT token validation failed, rejecting auth response');
          throw new Error('Received invalid JWT token from server');
        }

        console.log('JWT token validated successfully, storing auth state...');
        await Promise.all([
          config.storage.setToken(response.token),
          config.storage.setRefreshToken(response.refreshToken),
          config.storage.setUser(response.user),
        ]);

        console.log('Auth state stored, updating context state...');
        setToken(response.token);
        setRefreshToken(response.refreshToken);
        setUser(response.user);
        // localizationService.setLanguage(response.user.culture || 'en');
        console.log('Auth state updated successfully - user should now be logged in');
      } catch (error) {
        console.error('Failed to handle auth response:', error);
        console.log('Clearing auth state due to error');
        // Clear any partial state
        await clearAuthState();
        throw error;
      }
    };

    const login = async (credentials: LoginCredentials) => {
      try {
        const response = await fetch(`${config.apiBaseUrl}/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(credentials),
        });

        if (!response.ok) {
          throw new Error('Login failed');
        }

        const authResponse: AuthResponse = await response.json();
        await handleAuthResponse(authResponse);
      } catch (error) {
        console.error('Login failed:', error);
        throw error;
      }
    };

    const logout = async () => {
      try {
        console.log('Logging out user');

        // Call GPLogOut endpoint
        try {
          await fetch(`${config.apiBaseUrl}/logout/logout`, {
            method: 'GET',
            headers: { 
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json' 
            }
          });
        } catch (error) {
          console.warn('Failed to call GPLogOut:', error);
        }

        await clearAuthState();
      } catch (error) {
        console.error('Logout failed:', error);
        throw error;
      }
    };

    return (
      <AuthContext.Provider
        value={{
          user,
          token,
          refreshToken,
          isLoading,
          login,
          logout,
          handleAuthResponse,
          isTokenValid,
          refreshTokens,
        }}
      >
        {children}
      </AuthContext.Provider>
    );
  }

  return AuthProvider;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
} 